import { cloneElement, isValidElement, useId } from 'react';
import type { ReactElement, ReactNode } from 'react';
import { cx } from '../lib/cx';
import { Icon } from '../icons/Icon';

export interface FieldProps {
  label: ReactNode;
  /** Guidance shown between the label and the control — read it before you type.
   *  Stays visible alongside an error: "must be at least 8 characters" is most
   *  useful precisely while the value is wrong. */
  hint?: ReactNode;
  /** Error message, shown under the control — also flips it to its invalid style. */
  error?: ReactNode;
  required?: boolean;
  /** Show a subtle "(optional)" next to the label. */
  optional?: boolean;
  disabled?: boolean;
  className?: string;
  /** A single form control. Its id / aria-describedby / aria-invalid are wired
   *  automatically; the label points at it. */
  children: ReactNode;
  /** Override the generated control id (e.g. for a radio group's label). */
  htmlFor?: string;
}

/**
 * Label + hint + error wrapper (brief §4.2). Auto-associates the label and
 * describedby ids with the control it wraps.
 *
 * Vertical order is **label → hint → control → error**, deliberately: guidance
 * belongs before the answer, feedback after it.
 */
export function Field({ label, hint, error, required, optional, disabled, className, children, htmlFor }: FieldProps) {
  const autoId = useId();
  const controlId = htmlFor ?? autoId;
  const hintId = hint ? `${controlId}-hint` : undefined;
  const errorId = error ? `${controlId}-error` : undefined;
  const describedBy = [hintId, errorId].filter(Boolean).join(' ') || undefined;

  let control = children;
  // Wire the single child control without the consumer repeating ids.
  if (isValidElement(children) && !htmlFor) {
    const child = children as ReactElement<Record<string, unknown>>;
    control = cloneElement(child, {
      id: (child.props.id as string) ?? controlId,
      'aria-describedby': cx(child.props['aria-describedby'] as string, describedBy) || undefined,
      'aria-invalid': error ? true : (child.props['aria-invalid'] as boolean | undefined),
      invalid: error ? true : (child.props.invalid as boolean | undefined),
      disabled: disabled ?? (child.props.disabled as boolean | undefined),
    });
  }

  return (
    <div className={cx('tc-field', disabled && 'tc-field--disabled', className)}>
      <label className="tc-field__label" htmlFor={controlId}>
        {label}
        {required && (
          <span className="tc-field__req" aria-hidden>
            *
          </span>
        )}
        {optional && <span className="tc-field__optional">(optional)</span>}
      </label>
      {/* Hint sits BETWEEN the label and the control: it explains what to type,
          so it has to be readable before you start typing. Below the control it
          was routinely missed — the eye goes label → input → next field, and by
          the time you reach the hint you have already answered. */}
      {hint && (
        <span className="tc-field__hint" id={hintId}>
          {hint}
        </span>
      )}
      {control}
      {/* The error stays BELOW: unlike the hint it is feedback about the value
          you just entered, so it belongs where you were last looking. */}
      {error && (
        <span className="tc-field__error" id={errorId} role="alert">
          <Icon name="alert-circle" size={14} />
          {error}
        </span>
      )}
    </div>
  );
}
